import * as React from 'react';
import { observer } from 'mobx-react';
import {
    translate,
    ITranslationsCollection,
    addContext,
    addConfig,
    configConsumer
} from '@estee/elc-service';
import classnames from 'classnames';
import { OrderRender } from '@estee/elc-layouts';
import { root } from '@estee/elc-universal-utils';
import { ComponentTypes } from '~constants/ComponentTypes';
import { ProductFullViewController } from '~controllers/ProductFullViewController';
import { getProductFullViewRegistry } from '~utils/orderableUtils/getViewRegistry';
import { classes } from '~utils/Classes';
import { productFullConfigs } from '~utils/orderableUtils/configs';
import { productFullTranslations } from '~utils/orderableUtils/translations';
import { mergeConfigsFor } from '~utils/ConfigUtils';
import { IProductFull } from '~interfaces/IProductFull';
import { IOrderConfigCollection, IProductFullFeatureData } from '~interfaces/IOrderableComponents';
import { ProductFullWrapper } from './ProductFullStyles';

@addContext()
@addConfig(productFullConfigs, mergeConfigsFor(ComponentTypes.PRODUCT_FULL))
@configConsumer([
    'componentElementsOrderConfig',
    'contentServiceNodeIds',
    'imageCarouselSettings',
    'productId',
    'freeShippingAndReturn',
    'features',
    'vtoFeature',
    'engraving',
    'productDescription',
    'productInfoPopupConfig',
    'sizePicker',
    'hideSizeWhenSingular',
    'showEmptyRatingsStars',
    'showPriceOnStickyAddToBag',
    'contentServiceNodeIds',
    'hideProductPricePerUnit',
    'features'
])
@translate(productFullTranslations)
@observer
class ProductFull extends React.Component<IProductFull> {
    public static displayName: string;
    private productFullOrder: IOrderConfigCollection;
    private viewController: ProductFullViewController;

    constructor(props: IProductFull) {
        super(props);
        const {
            viewController,
            config: {
                componentElementsOrderConfig: { productFull }
            }
        } = props;

        this.productFullOrder = productFull;
        this.viewController = viewController;

        this.viewController.initialize(props);
        this.viewController.preLoaded();
    }

    public async componentDidMount() {
        (root as Window).mPulse.sendTimer('PRODFullMount', performance.now());
        await this.viewController.loaded();
    }

    public render() {
        const { isLoaded } = this.props.viewController;
        if (!isLoaded) {
            return null;
        }
        const { product } = this.viewController.data;
        const productFullClassNames = classnames(classes('product-full', this.props.className), {
            'multiple-sizes': product.hasMultipleSizes,
            'single-size': !product.hasMultipleSizes && product.isSized,
            'multiple-shades': product.hasMultipleShades,
            'single-shade': !product.hasMultipleShades && product.isShaded
        });

        const featureConfig: IProductFullFeatureData = {
            ...this.viewController.data,
            translations: this.props.translations as ITranslationsCollection,
            setAutoReplenishOption: this.viewController.setAutoReplenishOption,
            handleFavoriteClick: this.viewController.handleFavoriteClick,
            getCustomSizeMedia: this.viewController.getCustomSizeMedia,
            togglePerfectShades: this.viewController.togglePerfectShades,
            navigateToProduct: this.viewController.navigateToProduct,
            handleShadeSelect: this.viewController.onShadeSelect,
            handleSizeSelect: this.viewController.onSizeSelect,
            emitSocialShareIconTriggered: this.viewController.emitSocialShareIconTriggered,
            selectShade: this.viewController.selectShade,
            handleLiveChatCTAClick: this.viewController.handleLiveChatCTAClick,
            setShowStickyAddToBag: this.viewController.setShowStickyAddToBag,
            productUpdateQuantity: this.viewController.productUpdateQuantity,
            toggleProductInfoPopup: this.viewController.toggleProductInfoPopup,
            toggleDescription: this.viewController.toggleDescription,
            componentType: ComponentTypes.PRODUCT_FULL,
            setCurrentTabId: this.viewController.setCurrentTabId
        };

        return (
            <ProductFullWrapper className={productFullClassNames}>
                {Object.keys(this.productFullOrder).map((sectionName: string) => (
                    <div className={classes(`${sectionName}-section`)} key={sectionName}>
                        <OrderRender
                            data-test-id={`${sectionName}-section`}
                            orderConfig={this.productFullOrder[sectionName]}
                            serviceName={'elc-service-prodcat'}
                            viewRegistry={getProductFullViewRegistry<IProductFullFeatureData>(
                                featureConfig
                            )}
                        />
                    </div>
                ))}
            </ProductFullWrapper>
        );
    }
}

ProductFull.displayName = 'ProductFull';

export default ProductFull;
